import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Autocomplete,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Tooltip,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Add, Edit, Delete, Search, ImageNotSupported, Storefront, Link as LinkIcon, PhotoLibrary, Visibility, VisibilityOff, Sync as SyncIcon, LocalOffer as OfferIcon } from '@mui/icons-material';
import AdminLayout from '../../components/AdminLayout';
import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc, query, orderBy, getFirestore } from 'firebase/firestore';
import app from '../../config/firebase';
import {
  sincronizarProductoPublico,
  sincronizarProductosPublicos,
  eliminarProductoPublico,
  obtenerUltimoErrorSincronizacion,
  descuentoDe,
  precioFinal
} from '../../services/petshopPublico';

const db = getFirestore(app);

const COLECCION = 'petshop';
const SIN_CATEGORIA = '__sin_categoria__';
const MAX_FIRESTORE_BYTES = 500000; // límite seguro por imagen embebida (base64)

const formatoMoneda = (valor) =>
  `$${Number(valor || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;

const itemVacio = {
  titulo: '',
  categoria: '',
  descripcion: '',
  precioCompra: '',
  precioVenta: '',
  cantidad: '',
  imagenURL: '',
  visible: true
};

// Redimensiona y comprime una imagen a base64 JPEG
const compressImage = (file, maxSize, quality) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      const ratio = Math.min(1, maxSize / Math.max(width, height));
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(objectUrl);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('No se pudo leer la imagen'));
    };
    img.src = objectUrl;
  });

const Petshop = () => {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // En celular y tablet pequeña la tabla de 5 columnas no cabe: se muestran tarjetas
  const usarTarjetas = useMediaQuery(theme.breakpoints.down('md'));
  const fileInputRef = useRef(null);

  const [productos, setProductos] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [itemSeleccionado, setItemSeleccionado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [procesandoImagen, setProcesandoImagen] = useState(false);
  const [modoImagen, setModoImagen] = useState('url');
  const [confirmDelete, setConfirmDelete] = useState({ open: false, item: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [nuevoItem, setNuevoItem] = useState(itemVacio);
  const [categoriaFiltro, setCategoriaFiltro] = useState('todas');
  const [sincronizando, setSincronizando] = useState(false);
  const [descuentoDialog, setDescuentoDialog] = useState({ open: false, item: null, valor: '' });

  const notificar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const cargarProductos = async () => {
    setCargando(true);
    try {
      const q = query(collection(db, COLECCION), orderBy('titulo'));
      const snapshot = await getDocs(q);
      const lista = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setProductos(lista);
      // Carga inicial (una sola vez) de la vitrina pública con los productos existentes
      try {
        if (!localStorage.getItem('petshopPublicoV3')) {
          sincronizarProductosPublicos(lista).then((ok) => {
            if (ok) localStorage.setItem('petshopPublicoV3', '1');
            else notificar(`No se pudo actualizar la vitrina pública: ${obtenerUltimoErrorSincronizacion()}`, 'error');
          });
        }
      } catch (e) {
        console.error('No se pudo iniciar la carga de la vitrina pública:', e);
      }
    } catch (error) {
      console.error('Error al cargar productos del petshop:', error);
      notificar('Error al cargar los productos: ' + error.message, 'error');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarProductos();
  }, []);

  const categoriaDe = (p) => (p.categoria || '').trim();
  const categorias = [...new Set(productos.map(categoriaDe).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, 'es')
  );
  const haySinCategoria = productos.some((p) => !categoriaDe(p));
  // Si la categoría elegida ya no existe (p. ej. se editó el último producto), vuelve a "todas"
  const filtroEfectivo =
    categoriaFiltro === 'todas' ||
    (categoriaFiltro === SIN_CATEGORIA && haySinCategoria) ||
    categorias.includes(categoriaFiltro)
      ? categoriaFiltro
      : 'todas';

  const productosFiltrados = productos.filter((p) => {
    const texto = busqueda.toLowerCase();
    const coincideTexto =
      (p.titulo || '').toLowerCase().includes(texto) ||
      (p.descripcion || '').toLowerCase().includes(texto) ||
      categoriaDe(p).toLowerCase().includes(texto);
    const coincideCategoria =
      filtroEfectivo === 'todas' ||
      (filtroEfectivo === SIN_CATEGORIA ? !categoriaDe(p) : categoriaDe(p) === filtroEfectivo);
    return coincideTexto && coincideCategoria;
  });

  const handleOpenDialog = (item = null) => {
    if (item) {
      setItemSeleccionado(item);
      setNuevoItem({ ...itemVacio, ...item });
      setModoImagen(item.imagenURL && item.imagenURL.startsWith('data:') ? 'archivo' : 'url');
    } else {
      setItemSeleccionado(null);
      setNuevoItem(itemVacio);
      setModoImagen('url');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setGuardando(false);
    setProcesandoImagen(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNuevoItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    // Limpiar para poder volver a elegir el mismo archivo
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      notificar('Por favor selecciona un archivo de imagen válido', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      notificar('La imagen es demasiado grande. Máximo 10MB permitido', 'error');
      return;
    }

    setProcesandoImagen(true);
    try {
      // Reintenta con más compresión hasta caber en el límite
      const intentos = [[600, 0.6], [400, 0.4], [300, 0.3]];
      let resultado = null;
      for (const [maxSize, quality] of intentos) {
        const base64 = await compressImage(file, maxSize, quality);
        if (Math.round((base64.length * 3) / 4) <= MAX_FIRESTORE_BYTES) {
          resultado = base64;
          break;
        }
      }
      if (!resultado) {
        notificar('La imagen no se pudo comprimir lo suficiente. Intenta con una más pequeña', 'error');
        return;
      }
      setNuevoItem((prev) => ({ ...prev, imagenURL: resultado }));
    } catch (error) {
      console.error('Error al procesar la imagen:', error);
      notificar('Error al procesar la imagen: ' + error.message, 'error');
    } finally {
      setProcesandoImagen(false);
    }
  };

  const handleGuardarItem = async () => {
    if (guardando) return;

    const titulo = nuevoItem.titulo.trim();
    if (!titulo) {
      notificar('Error: El título del producto es requerido', 'error');
      return;
    }

    const cantidad = nuevoItem.cantidad === '' ? 0 : Number(nuevoItem.cantidad);
    const precioCompra = nuevoItem.precioCompra === '' ? 0 : Number(nuevoItem.precioCompra);
    const precioVenta = nuevoItem.precioVenta === '' ? 0 : Number(nuevoItem.precioVenta);
    if (cantidad < 0 || precioCompra < 0 || precioVenta < 0) {
      notificar('Error: No se permiten valores negativos en cantidad o precios', 'error');
      return;
    }

    // Reutiliza la ortografía de una categoría existente (sin distinguir mayúsculas)
    const categoriaEscrita = (nuevoItem.categoria || '').trim();
    const categoriaNormalizada =
      categorias.find((c) => c.toLowerCase() === categoriaEscrita.toLowerCase()) || categoriaEscrita;

    setGuardando(true);
    try {
      const { id, ...resto } = nuevoItem;
      const datos = {
        ...resto,
        titulo,
        categoria: categoriaNormalizada,
        visible: nuevoItem.visible !== false,
        descripcion: (nuevoItem.descripcion || '').trim(),
        imagenURL: (nuevoItem.imagenURL || '').trim(),
        cantidad,
        precioCompra,
        precioVenta
      };

      if (itemSeleccionado) {
        await updateDoc(doc(db, COLECCION, itemSeleccionado.id), datos);
        const sincronizado = await sincronizarProductoPublico(itemSeleccionado.id);
        if (sincronizado) notificar(`Producto "${titulo}" actualizado correctamente`);
        else notificar(`Producto "${titulo}" guardado, pero no se pudo actualizar la vitrina pública (${obtenerUltimoErrorSincronizacion()})`, 'warning');
      } else {
        const nuevoRef = await addDoc(collection(db, COLECCION), { ...datos, fechaCreacion: new Date().toISOString() });
        const sincronizado = await sincronizarProductoPublico(nuevoRef.id);
        if (sincronizado) notificar(`Producto "${titulo}" agregado correctamente`);
        else notificar(`Producto "${titulo}" guardado, pero no se pudo actualizar la vitrina pública (${obtenerUltimoErrorSincronizacion()})`, 'warning');
      }

      handleCloseDialog();
      cargarProductos();
    } catch (error) {
      console.error('Error al guardar el producto:', error);
      notificar(`Error al ${itemSeleccionado ? 'actualizar' : 'agregar'} el producto: ${error.message}`, 'error');
    } finally {
      setGuardando(false);
    }
  };

  // Vuelve a copiar a la web todos los productos visibles y quita los que ya no existen u están ocultos
  const handleSincronizarVitrina = async () => {
    setSincronizando(true);
    const ok = await sincronizarProductosPublicos(productos);
    const visibles = productos.filter((p) => p.visible !== false).length;
    if (ok) notificar(`Vitrina pública actualizada: ${visibles} producto(s) visible(s) en la web`);
    else notificar(`No se pudo actualizar la vitrina pública: ${obtenerUltimoErrorSincronizacion()}`, 'error');
    setSincronizando(false);
  };

  // Descuento: se guarda como porcentaje y se refleja en las tarjetas públicas
  const abrirDescuento = (item) => {
    const actual = descuentoDe(item);
    setDescuentoDialog({ open: true, item, valor: actual ? String(actual) : '' });
  };

  const cerrarDescuento = () => setDescuentoDialog({ open: false, item: null, valor: '' });

  const guardarDescuento = async (quitar = false) => {
    const { item, valor } = descuentoDialog;
    if (!item) return;
    const porcentaje = quitar ? 0 : Number(valor);
    if (!Number.isInteger(porcentaje) || porcentaje < 0 || porcentaje > 99) {
      notificar('El descuento debe ser un número entero entre 1 y 99', 'error');
      return;
    }
    try {
      await updateDoc(doc(db, COLECCION, item.id), { descuento: porcentaje });
      const sincronizado = await sincronizarProductoPublico(item.id);
      setProductos((prev) => prev.map((p) => (p.id === item.id ? { ...p, descuento: porcentaje } : p)));
      const mensaje = porcentaje
        ? `Descuento del ${porcentaje}% aplicado a "${item.titulo}"`
        : `Descuento quitado de "${item.titulo}"`;
      if (sincronizado) notificar(mensaje);
      else notificar(`${mensaje}, pero no se pudo actualizar la vitrina pública (${obtenerUltimoErrorSincronizacion()})`, 'warning');
      cerrarDescuento();
    } catch (error) {
      console.error('Error al guardar el descuento:', error);
      notificar('Error al guardar el descuento: ' + error.message, 'error');
    }
  };

  // Muestra u oculta un producto en la sección pública del Petshop
  const handleToggleVisible = async (item) => {
    const mostrar = item.visible === false;
    try {
      await updateDoc(doc(db, COLECCION, item.id), { visible: mostrar });
      await sincronizarProductoPublico(item.id);
      setProductos((prev) => prev.map((p) => (p.id === item.id ? { ...p, visible: mostrar } : p)));
      notificar(mostrar ? `"${item.titulo}" ahora se muestra al público` : `"${item.titulo}" ahora está oculto al público`);
    } catch (error) {
      console.error('Error al cambiar la visibilidad del producto:', error);
      notificar('Error al cambiar la visibilidad: ' + error.message, 'error');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete.item) return;
    try {
      await deleteDoc(doc(db, COLECCION, confirmDelete.item.id));
      await eliminarProductoPublico(confirmDelete.item.id);
      notificar(`Producto "${confirmDelete.item.titulo}" eliminado correctamente`);
      cargarProductos();
    } catch (error) {
      console.error('Error al eliminar el producto:', error);
      notificar('Error al eliminar el producto: ' + error.message, 'error');
    } finally {
      setConfirmDelete({ open: false, item: null });
    }
  };

  const abrirImagenGrande = (src) => {
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0,0,0,0.8); display: flex; justify-content: center;
      align-items: center; z-index: 9999; cursor: pointer;
    `;
    const img = document.createElement('img');
    img.src = src;
    img.style.cssText = 'max-width: 90%; max-height: 90%; border-radius: 8px;';
    modal.appendChild(img);
    modal.onclick = () => document.body.removeChild(modal);
    document.body.appendChild(modal);
  };

  const campoSx = { '& .MuiOutlinedInput-root': { borderRadius: 1.5 } };
  const botonRosaSx = {
    backgroundColor: '#c46494',
    '&:hover': { backgroundColor: '#a0527a' },
    textTransform: 'none',
    fontWeight: 'bold',
    borderRadius: 2
  };

  return (
    <AdminLayout>
      <Container maxWidth="lg" sx={{ mt: 0, mb: 2, px: isSmallMobile ? 0.5 : 1 }}>
        <Paper sx={{ p: isSmallMobile ? 1 : 2, mb: 2, mx: isSmallMobile ? 0.5 : 0 }}>
          <Typography
            variant={isSmallMobile ? 'h6' : 'h4'}
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              textAlign: 'center',
              color: '#c46494',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: isSmallMobile ? 1 : 2,
              fontSize: isSmallMobile ? '1.1rem' : '2rem'
            }}
          >
            <Storefront sx={{ mr: 1, color: '#602442', fontSize: isSmallMobile ? '1.25rem' : '2rem' }} />
            {isSmallMobile ? 'Petshop' : 'Gestión de Petshop'}
          </Typography>

          {cargando && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          )}

          <Grid container spacing={isSmallMobile ? 1 : 2} alignItems="center" sx={{ mb: isSmallMobile ? 2 : 3 }}>
            <Grid item xs={12} md={7}>
              <TextField
                label="Buscar producto"
                variant="outlined"
                size="small"
                fullWidth
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                InputProps={{ startAdornment: <Search color="action" sx={{ mr: 0.5 }} /> }}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(255, 255, 255, 0.8)' } }}
              />
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
              <Tooltip title="Copia a la web los productos visibles y quita los eliminados u ocultos">
                <span style={{ display: 'flex', flex: usarTarjetas ? 1 : 'none' }}>
                  <Button
                    fullWidth={usarTarjetas}
                    variant="outlined"
                    startIcon={sincronizando ? <CircularProgress size={16} /> : <SyncIcon />}
                    onClick={handleSincronizarVitrina}
                    disabled={sincronizando}
                    size="small"
                    sx={{
                      height: 40,
                      whiteSpace: 'nowrap',
                      borderColor: '#c46494',
                      color: '#c46494',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      borderRadius: 2,
                      '&:hover': { borderColor: '#a0527a', backgroundColor: 'rgba(196, 100, 148, 0.06)' }
                    }}
                  >
                    {isSmallMobile ? 'Web' : 'Actualizar web'}
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                size="small"
                sx={{ ...botonRosaSx, height: 40, whiteSpace: 'nowrap', flex: { xs: 1, md: 'none' }, minWidth: isSmallMobile ? 90 : 160 }}
              >
                {isSmallMobile ? 'Añadir' : 'Agregar Producto'}
              </Button>
            </Grid>
          </Grid>

          {productos.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: usarTarjetas ? 'nowrap' : 'wrap',
                overflowX: usarTarjetas ? 'auto' : 'visible',
                // width 0 + minWidth 100%: la fila deslizable no ensancha la página
                width: usarTarjetas ? 0 : 'auto',
                minWidth: usarTarjetas ? '100%' : 0,
                pb: usarTarjetas ? 1 : 0,
                mb: 2,
                '& .MuiChip-root': { flexShrink: 0 }
              }}
            >
              {[
                { clave: 'todas', etiqueta: `Todas (${productos.length})` },
                ...categorias.map((c) => ({
                  clave: c,
                  etiqueta: `${c} (${productos.filter((p) => categoriaDe(p) === c).length})`
                })),
                ...(haySinCategoria
                  ? [{ clave: SIN_CATEGORIA, etiqueta: `Sin categoría (${productos.filter((p) => !categoriaDe(p)).length})` }]
                  : [])
              ].map(({ clave, etiqueta }) => {
                const activo = filtroEfectivo === clave;
                return (
                  <Chip
                    key={clave}
                    label={etiqueta}
                    clickable
                    onClick={() => setCategoriaFiltro(clave)}
                    variant={activo ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: 'bold',
                      borderColor: '#c46494',
                      color: activo ? 'white' : '#c46494',
                      backgroundColor: activo ? '#c46494' : 'transparent',
                      '&:hover': { backgroundColor: activo ? '#a0527a' : 'rgba(196, 100, 148, 0.08)' }
                    }}
                  />
                );
              })}
            </Box>
          )}

          {usarTarjetas ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {productosFiltrados.map((item) => {
                const conDescuento = descuentoDe(item) > 0 && item.precioVenta > 0;
                return (
                  <Paper
                    key={item.id}
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2, borderColor: '#f0d5e4', opacity: item.visible === false ? 0.7 : 1 }}
                  >
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      {item.imagenURL ? (
                        <Box
                          component="img"
                          src={item.imagenURL}
                          alt={item.titulo}
                          loading="lazy"
                          onClick={() => abrirImagenGrande(item.imagenURL)}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                          }}
                          sx={{
                            width: 76,
                            height: 76,
                            flexShrink: 0,
                            objectFit: 'contain',
                            borderRadius: 1.5,
                            border: '1px solid #e0e0e0',
                            bgcolor: '#f8f8f8',
                            cursor: 'pointer'
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 76,
                            height: 76,
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f0f0f0',
                            borderRadius: 1.5,
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          <ImageNotSupported sx={{ color: '#bdbdbd', fontSize: 28 }} />
                        </Box>
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ wordBreak: 'break-word', lineHeight: 1.25, mb: 0.5 }}>
                          {item.titulo}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                          {categoriaDe(item) && (
                            <Chip
                              label={categoriaDe(item)}
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem', backgroundColor: 'rgba(196, 100, 148, 0.12)', color: '#602442' }}
                            />
                          )}
                          {item.visible === false && (
                            <Chip
                              icon={<VisibilityOff sx={{ fontSize: '0.9rem !important' }} />}
                              label="Oculto"
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem', backgroundColor: '#eeeeee', color: '#616161' }}
                            />
                          )}
                        </Box>
                        {item.descripcion && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              wordBreak: 'break-word'
                            }}
                          >
                            {item.descripcion}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 1.25,
                        p: 1,
                        borderRadius: 1.5,
                        backgroundColor: 'rgba(196, 100, 148, 0.06)'
                      }}
                    >
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Stock
                        </Typography>
                        <Typography fontWeight="bold" color={item.cantidad === 0 ? 'error' : 'text.primary'}>
                          {item.cantidad}
                          {item.cantidad === 0 && (
                            <Typography component="span" variant="caption" color="error" sx={{ ml: 0.75 }}>
                              Agotado
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        {conDescuento && (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                            <Chip
                              label={`-${descuentoDe(item)}%`}
                              size="small"
                              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 'bold', backgroundColor: '#e53935', color: 'white' }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                              {formatoMoneda(item.precioVenta)}
                            </Typography>
                          </Box>
                        )}
                        <Typography fontWeight="bold" color="primary" sx={{ fontSize: '1rem', lineHeight: 1.2 }}>
                          {item.precioVenta > 0 ? formatoMoneda(precioFinal(item)) : 'Sin precio'}
                        </Typography>
                        {item.precioCompra > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Costo: {formatoMoneda(item.precioCompra)}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', mt: 1, pt: 0.5, borderTop: '1px solid #f3e3ec' }}>
                      <IconButton
                        aria-label="Descuento"
                        onClick={() => abrirDescuento(item)}
                        sx={{ flex: 1, borderRadius: 1.5, color: descuentoDe(item) > 0 ? '#e53935' : '#9e9e9e' }}
                      >
                        <OfferIcon />
                      </IconButton>
                      <IconButton
                        aria-label={item.visible === false ? 'Mostrar al público' : 'Ocultar al público'}
                        onClick={() => handleToggleVisible(item)}
                        sx={{ flex: 1, borderRadius: 1.5, color: item.visible === false ? '#9e9e9e' : '#2e7d32' }}
                      >
                        {item.visible === false ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                      <IconButton aria-label="Editar" color="primary" onClick={() => handleOpenDialog(item)} sx={{ flex: 1, borderRadius: 1.5 }}>
                        <Edit />
                      </IconButton>
                      <IconButton
                        aria-label="Eliminar"
                        color="error"
                        onClick={() => setConfirmDelete({ open: true, item })}
                        sx={{ flex: 1, borderRadius: 1.5 }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Paper>
                );
              })}
              {!cargando && productosFiltrados.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Storefront sx={{ fontSize: 48, color: 'text.disabled' }} />
                  <Typography variant="h6" color="text.secondary">
                    No se encontraron productos
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {busqueda ? 'Intenta ajustar la búsqueda' : 'Agrega tu primer producto al petshop'}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
          <TableContainer component={Paper}>
            <Table aria-label="tabla de petshop" size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 64, p: 0.5, fontWeight: 'bold' }}>Foto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Producto</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Stock</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Precio</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productosFiltrados.map((item) => (
                  <TableRow key={item.id} hover sx={{ opacity: item.visible === false ? 0.6 : 1 }}>
                    <TableCell sx={{ p: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {item.imagenURL ? (
                          <Box
                            component="img"
                            src={item.imagenURL}
                            alt={item.titulo}
                            loading="lazy"
                            onClick={() => abrirImagenGrande(item.imagenURL)}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                            }}
                            sx={{
                              width: 48,
                              height: 48,
                              objectFit: 'contain',
                              borderRadius: 1,
                              border: '1px solid #e0e0e0',
                              bgcolor: '#f8f8f8',
                              cursor: 'pointer'
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#f0f0f0',
                              borderRadius: 1,
                              border: '1px solid #e0e0e0'
                            }}
                          >
                            <ImageNotSupported sx={{ color: '#bdbdbd', fontSize: 22 }} />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: isSmallMobile ? 140 : 360 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>
                        {item.titulo}
                      </Typography>
                      {item.visible === false && (
                        <Chip
                          icon={<VisibilityOff sx={{ fontSize: '0.9rem !important' }} />}
                          label="Oculto al público"
                          size="small"
                          sx={{ mb: 0.5, mr: 0.5, height: 20, fontSize: '0.7rem', backgroundColor: '#eeeeee', color: '#616161' }}
                        />
                      )}
                      {categoriaDe(item) && (
                        <Chip
                          label={categoriaDe(item)}
                          size="small"
                          sx={{ mb: 0.5, height: 20, fontSize: '0.7rem', backgroundColor: 'rgba(196, 100, 148, 0.12)', color: '#602442' }}
                        />
                      )}
                      {item.descripcion && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-word'
                          }}
                        >
                          {item.descripcion}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="bold" color={item.cantidad === 0 ? 'error' : 'text.primary'}>
                        {item.cantidad}
                      </Typography>
                      {item.cantidad === 0 && (
                        <Typography variant="caption" color="error" display="block">
                          Agotado
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {descuentoDe(item) > 0 && item.precioVenta > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                          <Chip
                            label={`-${descuentoDe(item)}%`}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold', backgroundColor: '#e53935', color: 'white' }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                            {formatoMoneda(item.precioVenta)}
                          </Typography>
                        </Box>
                      )}
                      <Typography fontWeight="bold" color="primary" sx={{ fontSize: '0.9rem' }}>
                        {item.precioVenta > 0 ? formatoMoneda(precioFinal(item)) : 'Sin precio'}
                      </Typography>
                      {item.precioCompra > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Costo: {formatoMoneda(item.precioCompra)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={descuentoDe(item) > 0 ? `Descuento del ${descuentoDe(item)}% (editar)` : 'Aplicar descuento'}>
                        <IconButton
                          size="small"
                          onClick={() => abrirDescuento(item)}
                          sx={{ color: descuentoDe(item) > 0 ? '#e53935' : '#9e9e9e' }}
                        >
                          <OfferIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={item.visible === false ? 'Mostrar al público' : 'Ocultar al público'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleVisible(item)}
                          sx={{ color: item.visible === false ? '#9e9e9e' : '#2e7d32' }}
                        >
                          {item.visible === false ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </Tooltip>
                      <IconButton color="primary" size="small" onClick={() => handleOpenDialog(item)}>
                        <Edit />
                      </IconButton>
                      <IconButton color="error" size="small" onClick={() => setConfirmDelete({ open: true, item })}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!cargando && productosFiltrados.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Storefront sx={{ fontSize: 48, color: 'text.disabled' }} />
                      <Typography variant="h6" color="text.secondary">
                        No se encontraron productos
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {busqueda ? 'Intenta ajustar la búsqueda' : 'Agrega tu primer producto al petshop'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>

        {/* Diálogo para agregar/editar producto */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          fullScreen={isSmallMobile}
          PaperProps={{ sx: { borderRadius: isSmallMobile ? 0 : 2 } }}
        >
          <DialogTitle
            sx={{
              backgroundColor: '#c46494',
              color: 'white',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Storefront sx={{ mr: 1 }} />
            {itemSeleccionado ? 'Editar Producto' : 'Agregar Nuevo Producto'}
          </DialogTitle>
          <DialogContent sx={{ px: isSmallMobile ? 2 : 3, pt: 3, pb: 2 }}>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="titulo"
                  label="Título"
                  fullWidth
                  required
                  margin="dense"
                  value={nuevoItem.titulo}
                  onChange={handleInputChange}
                  sx={campoSx}
                />
                <Autocomplete
                  freeSolo
                  options={categorias}
                  inputValue={nuevoItem.categoria || ''}
                  onInputChange={(_, valor) => setNuevoItem((prev) => ({ ...prev, categoria: valor }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Categoría"
                      margin="dense"
                      placeholder="Elige una o escribe una nueva"
                      sx={campoSx}
                    />
                  )}
                />
                <TextField
                  name="descripcion"
                  label="Descripción"
                  fullWidth
                  multiline
                  minRows={3}
                  margin="dense"
                  value={nuevoItem.descripcion}
                  onChange={handleInputChange}
                  sx={campoSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="precioCompra"
                  label="Precio de Compra (COP)"
                  type="number"
                  fullWidth
                  margin="dense"
                  value={nuevoItem.precioCompra}
                  onChange={handleInputChange}
                  InputProps={{
                    inputProps: { min: 0, step: 'any', onWheel: (e) => e.target.blur() },
                    startAdornment: <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                  }}
                  sx={campoSx}
                />
                <TextField
                  name="precioVenta"
                  label="Precio de Venta (COP)"
                  type="number"
                  fullWidth
                  required
                  margin="dense"
                  value={nuevoItem.precioVenta}
                  onChange={handleInputChange}
                  InputProps={{
                    inputProps: { min: 0, step: 'any', onWheel: (e) => e.target.blur() },
                    startAdornment: <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                  }}
                  sx={campoSx}
                />
                <TextField
                  name="cantidad"
                  label="Cantidad en Stock"
                  type="number"
                  fullWidth
                  required
                  margin="dense"
                  value={nuevoItem.cantidad}
                  onChange={handleInputChange}
                  InputProps={{ inputProps: { min: 0, step: '1', onWheel: (e) => e.target.blur() } }}
                  sx={campoSx}
                />
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={
                    <Switch
                      checked={nuevoItem.visible !== false}
                      onChange={(e) => setNuevoItem((prev) => ({ ...prev, visible: e.target.checked }))}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#c46494' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#c46494' }
                      }}
                    />
                  }
                  label="Mostrar en la sección pública"
                />
              </Grid>
            </Grid>

            {/* Imagen: por URL o desde el dispositivo */}
            <Box
              sx={{
                mt: 3,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                p: isSmallMobile ? 2 : 3,
                backgroundColor: 'rgba(196, 100, 148, 0.02)'
              }}
            >
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: '#602442' }}>
                Foto del Producto
              </Typography>

              <ToggleButtonGroup
                value={modoImagen}
                exclusive
                size="small"
                onChange={(_, valor) => valor && setModoImagen(valor)}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="url" sx={{ textTransform: 'none' }}>
                  <LinkIcon fontSize="small" sx={{ mr: 0.5 }} /> Con URL
                </ToggleButton>
                <ToggleButton value="archivo" sx={{ textTransform: 'none' }}>
                  <PhotoLibrary fontSize="small" sx={{ mr: 0.5 }} /> Desde el dispositivo
                </ToggleButton>
              </ToggleButtonGroup>

              {modoImagen === 'url' ? (
                <TextField
                  name="imagenURL"
                  label="URL de la imagen"
                  type="url"
                  fullWidth
                  size="small"
                  // Si la imagen actual es un archivo subido, no volcar el base64 en el campo
                  value={nuevoItem.imagenURL.startsWith('data:') ? '' : nuevoItem.imagenURL}
                  onChange={handleInputChange}
                  helperText="Pega el enlace de una imagen del producto"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, backgroundColor: 'white' } }}
                />
              ) : (
                <Box>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={procesandoImagen}
                    startIcon={procesandoImagen ? <CircularProgress size={16} /> : <PhotoLibrary />}
                    sx={{
                      borderColor: '#c46494',
                      color: '#c46494',
                      textTransform: 'none',
                      fontWeight: 'bold',
                      borderRadius: 2
                    }}
                  >
                    {procesandoImagen ? 'Procesando...' : 'Seleccionar imagen'}
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    En el celular también puedes tomar la foto con la cámara. Se optimiza automáticamente.
                  </Typography>
                </Box>
              )}

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                {nuevoItem.imagenURL ? (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={nuevoItem.imagenURL}
                      alt="Vista previa"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                      }}
                      sx={{
                        height: isSmallMobile ? 120 : 160,
                        maxWidth: '100%',
                        objectFit: 'contain',
                        border: '2px solid #e0e0e0',
                        borderRadius: 2,
                        backgroundColor: 'white',
                        p: 1
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setNuevoItem((prev) => ({ ...prev, imagenURL: '' }))}
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: '#d32f2f',
                        color: 'white',
                        '&:hover': { backgroundColor: '#b71c1c' },
                        width: 24,
                        height: 24
                      }}
                    >
                      <Delete sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: isSmallMobile ? 120 : 160,
                      width: '100%',
                      maxWidth: 300,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px dashed #ccc',
                      borderRadius: 2,
                      color: 'text.secondary'
                    }}
                  >
                    <ImageNotSupported sx={{ fontSize: 40, mb: 1, color: '#bdbdbd' }} />
                    <Typography variant="body2">Sin imagen</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={handleCloseDialog} color="inherit" sx={{ textTransform: 'none', fontWeight: 'bold' }}>
              Cancelar
            </Button>
            <Button
              onClick={handleGuardarItem}
              variant="contained"
              disabled={guardando || procesandoImagen}
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
              sx={botonRosaSx}
            >
              {guardando ? 'Guardando...' : itemSeleccionado ? 'Actualizar' : 'Guardar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ mt: 10 }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Descuento */}
        <Dialog open={descuentoDialog.open} onClose={cerrarDescuento} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ backgroundColor: '#c46494', color: 'white', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <OfferIcon /> Descuento
          </DialogTitle>
          <DialogContent>
            {descuentoDialog.item && (() => {
              const { item, valor } = descuentoDialog;
              const porcentaje = Number(valor);
              const valido = Number.isInteger(porcentaje) && porcentaje > 0 && porcentaje < 100;
              const conDescuento = valido ? precioFinal({ precioVenta: item.precioVenta, descuento: porcentaje }) : null;
              return (
                <Box sx={{ pt: 2 }}>
                  <Typography fontWeight="bold" sx={{ wordBreak: 'break-word' }}>{item.titulo}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Precio de venta: {formatoMoneda(item.precioVenta)}
                  </Typography>
                  {!(item.precioVenta > 0) && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Este producto no tiene precio de venta. Agrégalo primero para poder aplicar un descuento.
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {[10, 15, 20, 25, 30, 50].map((p) => (
                      <Chip
                        key={p}
                        label={`${p}%`}
                        clickable
                        onClick={() => setDescuentoDialog((prev) => ({ ...prev, valor: String(p) }))}
                        variant={porcentaje === p ? 'filled' : 'outlined'}
                        sx={{
                          fontWeight: 'bold',
                          borderColor: '#c46494',
                          color: porcentaje === p ? 'white' : '#c46494',
                          backgroundColor: porcentaje === p ? '#c46494' : 'transparent',
                          '&:hover': { backgroundColor: porcentaje === p ? '#a0527a' : 'rgba(196, 100, 148, 0.08)' }
                        }}
                      />
                    ))}
                  </Box>
                  <TextField
                    label="Descuento (%)"
                    type="number"
                    fullWidth
                    value={valor}
                    onChange={(e) => setDescuentoDialog((prev) => ({ ...prev, valor: e.target.value }))}
                    InputProps={{ inputProps: { min: 1, max: 99, step: 1, onWheel: (e) => e.target.blur() } }}
                    sx={campoSx}
                  />
                  {conDescuento !== null && item.precioVenta > 0 && (
                    <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, backgroundColor: 'rgba(196, 100, 148, 0.08)' }}>
                      <Typography variant="body2" color="text.secondary">Precio con descuento</Typography>
                      <Typography variant="h5" fontWeight="bold" sx={{ color: '#c46494' }}>
                        {formatoMoneda(conDescuento)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        El cliente ahorra {formatoMoneda(item.precioVenta - conDescuento)}
                      </Typography>
                    </Box>
                  )}
                  {conDescuento !== null && item.precioCompra > 0 && conDescuento < item.precioCompra && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Con este descuento el precio queda por debajo del costo ({formatoMoneda(item.precioCompra)}).
                    </Alert>
                  )}
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
            {descuentoDialog.item && descuentoDe(descuentoDialog.item) > 0 && (
              <Button color="error" onClick={() => guardarDescuento(true)} sx={{ textTransform: 'none', fontWeight: 'bold', mr: 'auto' }}>
                Quitar descuento
              </Button>
            )}
            <Button onClick={cerrarDescuento} color="inherit" sx={{ textTransform: 'none', fontWeight: 'bold' }}>
              Cancelar
            </Button>
            <Button
              onClick={() => guardarDescuento(false)}
              variant="contained"
              disabled={!(descuentoDialog.item && descuentoDialog.item.precioVenta > 0)}
              sx={botonRosaSx}
            >
              Aplicar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmación para eliminar */}
        <Dialog
          open={confirmDelete.open}
          onClose={() => setConfirmDelete({ open: false, item: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f', fontWeight: 'bold' }}>
            <Delete /> Confirmar Eliminación
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 1 }}>
              ¿Seguro que deseas eliminar <strong>{confirmDelete.item?.titulo}</strong> del petshop?
            </Typography>
            <Alert severity="warning">Esta acción no se puede deshacer.</Alert>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmDelete({ open: false, item: null })} variant="outlined">
              Cancelar
            </Button>
            <Button onClick={handleConfirmDelete} variant="contained" color="error">
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </AdminLayout>
  );
};

export default Petshop;
